/**
 * Record-level validation shared by every path that turns a batch of signals
 * (message path + topic records) into one visualizer panel: the on-page
 * workspace action handlers and the off-page deep-link builder. Keeping the
 * rules here prevents the two paths from drifting apart — the model must
 * observe the same outcome for the same input regardless of which page the
 * user is on.
 *
 * Node-level rules that need a hydrated topic tree (e.g. log column-conflict
 * checks) stay with the on-page handlers; everything expressible from raw
 * records lives here.
 */
import { PanelType } from "@/shared/state/visualization";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

import {
  CanonicalDataType,
  type CoordinateRole,
  geoSpecificRole,
  isGeoSpecificType,
  isPlottableCanonicalType,
  MessagePathRecord,
  namesFormCoordinatePair,
  RepresentationStorageFormat,
  TopicRecord,
} from "../topics";

export interface SignalEntry {
  messagePath: MessagePathRecord;
  topic: TopicRecord;
}

/**
 * One map track: the two records a map path is drawn from, in role order.
 *
 * Roles are settled here rather than downstream because a plain `number`
 * coordinate carries none in its data type, and `getGeoMessagePaths` reads
 * the first member of a path's data as the latitude. Handing it an ordered
 * pair is what makes that reading correct instead of a coin flip.
 */
export interface MapPair {
  latitude: SignalEntry;
  longitude: SignalEntry;
}

export interface InvalidSignal {
  messagePathId: string;
  reason: string;
}

/** The panel types creatable from message paths. */
export type SignalPanelType =
  | PanelType.Plot
  | PanelType.Log
  | PanelType.RawMessage
  | PanelType.Image
  | PanelType.Map;

export type SignalsValidation =
  | {
      ok: true;
      panelType: SignalPanelType;
      /**
       * The entries to create the panel from. May be longer than the input:
       * a lone latitude/longitude signal gets its corresponding pair member
       * bundled in, just as dragging a lone coordinate onto the workspace
       * does.
       */
      entries: SignalEntry[];
      /**
       * Map panels only: the validated tracks, one per latitude/longitude
       * pair, in role order. `entries` holds the same records flattened in
       * the same order, so a caller that only needs the record set (the
       * files a deep link opens) can ignore this.
       */
      mapPairs?: MapPair[];
    }
  | { ok: false; error: string; invalid: InvalidSignal[] };

/**
 * Whether a signal can be read in one of `formats`, mirroring
 * `MessagePathNode.findRepresentation`'s resolution order: the path's own
 * representations, then ancestor paths' (parent chain), then the topic's
 * default representation. Paths commonly carry no representations of their
 * own and inherit the topic default (e.g. ULog console_logs fields).
 */
function hasRepresentation(
  entry: SignalEntry,
  formats: RepresentationStorageFormat[],
): boolean {
  const { messagePath, topic } = entry;
  const matches = (
    representations: { storage_format: RepresentationStorageFormat }[],
  ) =>
    representations.some((representation) =>
      formats.includes(representation.storage_format),
    );

  if (matches(messagePath.representations)) {
    return true;
  }

  const isAncestor = (candidate: MessagePathRecord) =>
    candidate.path_in_schema.length < messagePath.path_in_schema.length &&
    candidate.path_in_schema.every(
      (segment, i) => messagePath.path_in_schema[i] === segment,
    );
  if (
    topic.message_paths.some(
      (candidate) =>
        isAncestor(candidate) && matches(candidate.representations),
    )
  ) {
    return true;
  }

  return (
    topic.default_representation !== null &&
    topic.default_representation !== undefined &&
    formats.includes(topic.default_representation.storage_format)
  );
}

/**
 * A record is a leaf when no other path in its topic extends its
 * path_in_schema. Mirrors MessagePathNode.isLeafNode() without needing a
 * hydrated tree.
 */
function isLeafRecord(
  topic: TopicRecord,
  messagePath: MessagePathRecord,
): boolean {
  const prefix = messagePath.path_in_schema;
  return !topic.message_paths.some(
    (candidate) =>
      candidate.path_in_schema.length > prefix.length &&
      prefix.every((segment, i) => candidate.path_in_schema[i] === segment),
  );
}

/** The field's own name: the last segment of its path in the schema. */
function leafName(messagePath: MessagePathRecord): string {
  return (
    messagePath.path_in_schema[messagePath.path_in_schema.length - 1] ??
    messagePath.message_path
  );
}

/** Whether two records are leaf fields of one parent path. */
function areSiblings(a: MessagePathRecord, b: MessagePathRecord): boolean {
  const parentOfA = a.path_in_schema.slice(0, -1);
  const parentOfB = b.path_in_schema.slice(0, -1);
  return (
    parentOfA.length === parentOfB.length &&
    parentOfA.every((segment, i) => parentOfB[i] === segment)
  );
}

/**
 * Read two entries as one map track, or return undefined when they are not a
 * latitude/longitude pair.
 *
 * Both members must be sibling fields of one topic — `pose.latitude` with
 * `home.longitude` would render a track that never existed. Given that, the
 * role comes from the data types when both carry one, and otherwise from the
 * field names ({@link namesFormCoordinatePair}), which is the only thing a
 * plain `number` coordinate offers.
 *
 * A geo-typed member whose type contradicts the names is refused rather than
 * reconciled: one of the two readings is wrong and nothing here can tell
 * which.
 */
function pairRole(a: SignalEntry, b: SignalEntry): MapPair | undefined {
  if (a.messagePath.message_path_id === b.messagePath.message_path_id) {
    return undefined;
  }
  if (a.topic.topic_id !== b.topic.topic_id) {
    return undefined;
  }
  if (!areSiblings(a.messagePath, b.messagePath)) {
    return undefined;
  }

  const inRoleOrder = (roleOfA: CoordinateRole): MapPair =>
    roleOfA === "latitude"
      ? { latitude: a, longitude: b }
      : { latitude: b, longitude: a };

  const typedA = geoSpecificRole(a.messagePath.canonical_data_type);
  const typedB = geoSpecificRole(b.messagePath.canonical_data_type);
  if (typedA !== undefined && typedB !== undefined) {
    // Both geo-typed: the types settle it, and the names are not consulted.
    // This is the pairing maps had before plain numbers were admitted.
    return typedA === typedB ? undefined : inRoleOrder(typedA);
  }

  const namedA = namesFormCoordinatePair(
    leafName(a.messagePath),
    leafName(b.messagePath),
  );
  if (namedA === undefined) {
    return undefined;
  }
  // Naming one member names the other: a pair is one of each.
  const namedB: CoordinateRole =
    namedA === "latitude" ? "longitude" : "latitude";
  if (typedA !== undefined && typedA !== namedA) {
    return undefined;
  }
  if (typedB !== undefined && typedB !== namedB) {
    return undefined;
  }
  return inRoleOrder(namedA);
}

/**
 * Why two coordinates the caller named together are not one track, phrased
 * against `other`. Follows {@link pairRole}'s checks in the same order, so
 * the reason given is the first rule the two actually fail.
 */
function pairRejectionReason(entry: SignalEntry, other: SignalEntry): string {
  const name = entry.messagePath.message_path;
  const otherName = other.messagePath.message_path;
  if (entry.topic.topic_id !== other.topic.topic_id) {
    return (
      `"${name}" belongs to a different topic than "${otherName}"; a map ` +
      `track's latitude and longitude must come from one topic.`
    );
  }
  if (!areSiblings(entry.messagePath, other.messagePath)) {
    return (
      `"${name}" is not a sibling of "${otherName}"; a map track's latitude ` +
      `and longitude must be fields of the same parent path.`
    );
  }
  const role = geoSpecificRole(entry.messagePath.canonical_data_type);
  if (
    role !== undefined &&
    role === geoSpecificRole(other.messagePath.canonical_data_type)
  ) {
    return (
      `"${name}" and "${otherName}" are both ${role}; a map track needs one ` +
      `latitude and one longitude.`
    );
  }
  return (
    `"${name}" and "${otherName}" do not form a latitude/longitude pair: ` +
    `their names differ by more than a lat/lon token, so which of them is ` +
    `which cannot be read from them.`
  );
}

/**
 * Every sibling record in `topic` that would complete `messagePath`'s pair.
 *
 * More than one is an ambiguity, not a choice: a topic carrying both `lon`
 * and `lng` beside one `lat` gives no ground to prefer either, and picking
 * the first would draw a map that looks right and is not.
 */
function findGeoPartnerRecords(
  topic: TopicRecord,
  messagePath: MessagePathRecord,
): MessagePathRecord[] {
  return topic.message_paths.filter(
    (candidate) =>
      pairRole({ messagePath, topic }, { messagePath: candidate, topic }) !==
      undefined,
  );
}

/**
 * The sibling record that completes a latitude/longitude pair, or undefined
 * when there is no single such record. Mirrors the drag-and-drop path's
 * `getCorrespondingMessagePath`, which searches the node's parent's children.
 */
export function findCorrespondingGeoRecord(
  topic: TopicRecord,
  messagePath: MessagePathRecord,
): MessagePathRecord | undefined {
  const partners = findGeoPartnerRecords(topic, messagePath);
  return partners.length === 1 ? partners[0] : undefined;
}

/**
 * Validate a single signal against the panel type it would join. Applied both
 * when creating a panel and when adding to an existing one, so the two
 * operations accept exactly the same signals. Returns a reason when the
 * signal cannot render on that panel type.
 *
 * Each arm checks the signal's data type as well as its structure. The data
 * type used to be guaranteed by the caller — a panel type derived from the
 * signals could only ever be one they suited — but a caller naming the panel
 * type explicitly (see {@link validateSignalsForNewPanel}) carries no such
 * guarantee, and without these checks an unrenderable panel is created and
 * reported as a success.
 */
export function validateSignalForPanelType(
  panelType: SignalPanelType,
  entry: SignalEntry,
): string | undefined {
  const { messagePath, topic } = entry;
  const canonicalType = messagePath.canonical_data_type;
  switch (panelType) {
    case PanelType.Plot:
      // Wider than the default rule on purpose: `arrayDefaultsToPlot`
      // decides what to open unasked, this decides what to render once
      // something named the panel — `panel_type`, add-to-panel, or a drag onto
      // an existing plot. So a byte array plots on request, never by default.
      if (!isPlottableCanonicalType(canonicalType)) {
        return `Data type "${canonicalType}" cannot be plotted.`;
      }
      if (!isLeafRecord(topic, messagePath)) {
        return `Message path "${messagePath.message_path}" has nested fields and cannot be plotted directly; plot one of its leaf fields instead.`;
      }
      return undefined;
    case PanelType.Image:
      if (canonicalType !== CanonicalDataType.Image) {
        return `Data type "${canonicalType}" is not an image.`;
      }
      if (!hasRepresentation(entry, [RepresentationStorageFormat.MCAP])) {
        return "The Image panel does not yet support visualizing this signal.";
      }
      return undefined;
    case PanelType.Log:
    case PanelType.RawMessage:
      // A log column renders through the same readers as a raw message, but
      // an image is never one; `isViewableAsConsoleLog` excludes it too.
      if (
        panelType === PanelType.Log &&
        canonicalType === CanonicalDataType.Image
      ) {
        return "An image cannot be shown as a log column.";
      }
      if (
        !hasRepresentation(entry, [
          RepresentationStorageFormat.MCAP,
          RepresentationStorageFormat.PARQUET,
        ])
      ) {
        return `Message path "${messagePath.message_path}" has no readable representation.`;
      }
      return undefined;
    case PanelType.Map:
      // A coordinate is either geo-typed, or a plain number whose role its
      // field name supplies (see `pairRole`). Nothing else has a reading as
      // a coordinate at all. This admits numbers rather than pairing them:
      // one that turns out to have no partner is rejected by
      // `validateMapSignals`, with a reason naming the field.
      //
      // `isViewableAsMap` still excludes numbers on the drag-and-drop path,
      // where a dropped field carries no role and there is nothing to pair
      // it against but a guess.
      if (
        !isGeoSpecificType(canonicalType) &&
        canonicalType !== CanonicalDataType.Number
      ) {
        return `Data type "${canonicalType}" cannot be shown on a map; a map needs a latitude/longitude pair.`;
      }
      return undefined;
    default: {
      const _exhaustive: never = panelType;
      return `Unhandled panel type: ${String(_exhaustive)}`;
    }
  }
}

/**
 * Validate a batch of signals destined for one NEW panel.
 *
 * 1. Rejects duplicates and mixed panel types with per-signal reasons.
 * 2. Rejects signals the resolved panel type cannot render
 *    ({@link validateSignalForPanelType}).
 * 3. Enforces same-topic constraints (log: topic name; raw message: topic id).
 * 4. For maps, groups the batch into latitude/longitude pairs, one per track
 *    ({@link validateMapSignals}) — bundling a pair member from the topic
 *    when a lone coordinate is given.
 *
 * `requestedPanelType` names the panel to build instead of the one the
 * signals' data types imply — a caller asking to plot a signal that defaults
 * to a raw view, or to read a plottable signal as raw text. It settles which
 * panel type the batch targets and so retires rule 1's mixed-type check;
 * every other rule still applies, and a signal the requested type cannot
 * render is rejected exactly as it would be by default.
 */
export function validateSignalsForNewPanel(
  inputEntries: SignalEntry[],
  requestedPanelType?: SignalPanelType,
): SignalsValidation {
  if (inputEntries.length === 0) {
    return {
      ok: false,
      error: "At least one message path is required to create a panel.",
      invalid: [],
    };
  }

  const invalid: InvalidSignal[] = [];

  const seen = new Set<string>();
  const entries: SignalEntry[] = [];
  for (const entry of inputEntries) {
    const id = entry.messagePath.message_path_id;
    if (seen.has(id)) {
      invalid.push({
        messagePathId: id,
        reason:
          "Duplicated in the request; each message path may appear only once.",
      });
      continue;
    }
    seen.add(id);
    entries.push(entry);
  }
  if (invalid.length > 0) {
    return {
      ok: false,
      error: "Some message paths cannot be opened.",
      invalid,
    };
  }

  let panelType: SignalPanelType;
  if (requestedPanelType !== undefined) {
    panelType = requestedPanelType;
  } else {
    const typed = entries.map((entry) => ({
      entry,
      panelType: determineDefaultPanelType(entry.messagePath),
    }));
    panelType = typed[0].panelType as SignalPanelType;
    if (typed.some((item) => item.panelType !== panelType)) {
      return {
        ok: false,
        error:
          "All message paths in one call must visualize as the same panel type. " +
          "Split the request into one call per panel, or name the panel type to " +
          "open them together.",
        invalid: typed.map((item) => ({
          messagePathId: item.entry.messagePath.message_path_id,
          reason: `Visualizes as a ${item.panelType} panel.`,
        })),
      };
    }
  }

  entries.forEach((entry) => {
    const reason = validateSignalForPanelType(panelType, entry);
    if (reason) {
      invalid.push({
        messagePathId: entry.messagePath.message_path_id,
        reason,
      });
    }
  });
  if (invalid.length > 0) {
    return {
      ok: false,
      error: `Some message paths cannot be shown on a ${panelType} panel.`,
      invalid,
    };
  }

  if (panelType === PanelType.Log || panelType === PanelType.RawMessage) {
    const first = entries[0].topic;
    entries.slice(1).forEach((entry) => {
      const sameTopic =
        panelType === PanelType.Log
          ? entry.topic.topic_name === first.topic_name
          : entry.topic.topic_id === first.topic_id;
      if (!sameTopic) {
        invalid.push({
          messagePathId: entry.messagePath.message_path_id,
          reason: `Cannot combine data from different topics on a ${panelType} panel.`,
        });
      }
    });
    if (invalid.length > 0) {
      return {
        ok: false,
        error: `All message paths on a ${panelType} panel must come from one topic.`,
        invalid,
      };
    }
  }

  if (panelType === PanelType.Map) {
    return validateMapSignals(entries);
  }

  return { ok: true, panelType, entries };
}

/**
 * Group a batch of coordinates into map tracks: one latitude/longitude pair
 * per track, in role order.
 *
 * A map panel holds a list of paths, so a batch may open a whole fleet at
 * once — twelve rovers' coordinates become twelve tracks on one map. Each
 * coordinate pairs with a partner named alongside it in the same batch, or
 * failing that with its sibling in the topic, bundled in as dragging a lone
 * coordinate onto the workspace does.
 *
 * Batch partners are preferred over bundled siblings so that naming both
 * members explicitly always means what it says, and never leaves one member
 * bundled against a third field while the other opens a track of its own.
 */
function validateMapSignals(entries: SignalEntry[]): SignalsValidation {
  const invalid: InvalidSignal[] = [];
  const pairs: MapPair[] = [];
  const consumed = new Set<string>();

  for (const entry of entries) {
    const messagePathId = entry.messagePath.message_path_id;
    if (consumed.has(messagePathId)) {
      continue;
    }
    consumed.add(messagePathId);

    let partnerInBatch: SignalEntry | undefined;
    let pairInBatch: MapPair | undefined;
    for (const candidate of entries) {
      if (consumed.has(candidate.messagePath.message_path_id)) {
        continue;
      }
      const pair = pairRole(entry, candidate);
      if (pair) {
        partnerInBatch = candidate;
        pairInBatch = pair;
        break;
      }
    }
    if (partnerInBatch && pairInBatch) {
      consumed.add(partnerInBatch.messagePath.message_path_id);
      pairs.push(pairInBatch);
      continue;
    }

    // A record already drawn as part of another track is not available to
    // this one. Two latitudes sharing one longitude would be two tracks built
    // from a partner only one of them owns — and the second latitude's real
    // partner is simply missing, which is what the caller needs told.
    const partnerRecords = findGeoPartnerRecords(
      entry.topic,
      entry.messagePath,
    ).filter((record) => !consumed.has(record.message_path_id));
    if (partnerRecords.length > 1) {
      invalid.push({
        messagePathId,
        reason:
          `Has ${partnerRecords.length} possible pair coordinates in topic ` +
          `"${entry.topic.topic_name}" (${partnerRecords
            .map((record) => `"${record.message_path}"`)
            .join(", ")}); name the intended one alongside it.`,
      });
      continue;
    }
    const bundled =
      partnerRecords.length === 1
        ? pairRole(entry, {
            messagePath: partnerRecords[0],
            topic: entry.topic,
          })
        : undefined;
    if (bundled) {
      consumed.add(partnerRecords[0].message_path_id);
      pairs.push(bundled);
      continue;
    }

    // Nothing pairs with this one. When exactly one other coordinate is left
    // unclaimed, the caller named two things that were meant to go together,
    // and saying which rule the two of them fail beats reporting each as
    // unpairable on its own.
    const unclaimed = entries.filter(
      (candidate) => !consumed.has(candidate.messagePath.message_path_id),
    );
    if (unclaimed.length === 1) {
      const other = unclaimed[0];
      consumed.add(other.messagePath.message_path_id);
      invalid.push({
        messagePathId: other.messagePath.message_path_id,
        reason: pairRejectionReason(other, entry),
      });
      continue;
    }

    invalid.push({
      messagePathId,
      reason:
        `No matching pair coordinate was found for ` +
        `"${entry.messagePath.message_path}" in topic ` +
        `"${entry.topic.topic_name}"; a map track needs a latitude/longitude ` +
        `pair. A map pairs a field with the sibling whose name differs from ` +
        `it only in lat/lon (for example "gps_lat" with "gps_lon"); name both ` +
        `members together when their names do not follow that pattern.`,
    });
  }

  if (invalid.length > 0) {
    return {
      ok: false,
      // A single reason is the whole story, and it is what the caller reads
      // first; several mean the batch had unrelated problems, listed per path.
      error:
        invalid.length === 1
          ? invalid[0].reason
          : "Some message paths could not be paired into map tracks.",
      invalid,
    };
  }

  return {
    ok: true,
    panelType: PanelType.Map,
    entries: pairs.flatMap((pair) => [pair.latitude, pair.longitude]),
    mapPairs: pairs,
  };
}
